import React from 'react'
import { Query } from 'react-apollo'
import { applicationFragment } from '../applications-grid/fragments'
import {
  VulnerabilitySeverityBreakdown,
  ApplicationLicense,
  CompliancePolicy,
  MetadataValue
} from '../applications-grid/shared'
import { AttackStatus } from '../attack-status'
import { Role } from '@contrast-security-inc/base-types'
import { ApplicationImportance } from '../application-importance'
import gql from 'graphql-tag'
import { PureQueryOptions } from 'apollo-client'
import { FormattedMessage } from 'react-intl'
import { GradeValue } from '../../graphql/schema/types/grade-value'

export const applicationQuery = gql`
  query GetApplication($organizationID: ID!, $applicationID: ID!) {
    application(
      organizationID: $organizationID
      applicationID: $applicationID
    ) {
      ...Application
    }
  }

  ${applicationFragment}
`

interface Application {
  readonly id: string
  readonly name: string
  readonly language: string
  readonly vulnerabilityBreakdown: VulnerabilitySeverityBreakdown
  readonly attackStatus: AttackStatus
  readonly hasAProtectedServer: boolean
  readonly license?: ApplicationLicense
  readonly attackTotal: number
  readonly role: Role
  readonly url: string
  readonly linesOfCode: string
  readonly importance: ApplicationImportance
  readonly technologies: string[]
  readonly lastActivity: string
  readonly online: boolean
  readonly archived: boolean
  readonly primary: boolean
  readonly moduleCount: number
  readonly violatedCompliancePolicies: CompliancePolicy[]
  readonly tags: string[]
  readonly customMetadataFields?: MetadataValue[]
  readonly scoreGrade: GradeValue
}

interface QueryData {
  readonly application: Application
}

interface QueryVariables {
  readonly organizationID: string
  readonly applicationID: string
}

export function ApplicationTooltipLoader({
  children,
  organizationID,
  applicationID
}: {
  readonly children: (props: {
    readonly application: Application
    readonly queryInfo: PureQueryOptions
  }) => React.ReactNode
  readonly organizationID: string
  readonly applicationID: string
}) {
  return (
    <Query<QueryData, QueryVariables>
      query={applicationQuery}
      variables={{
        organizationID,
        applicationID
      }}
      errorPolicy="all"
      fetchPolicy="cache-first"
    >
      {({ loading, error, data }) => {
        if (error) {
          throw new Error(error.message)
        }
        if (loading || !data || !data.application) {
          return <FormattedMessage id="detail_applicationTooltip_loading" />
        }

        return children({
          application: data.application,
          queryInfo: {
            query: applicationQuery,
            variables: {
              organizationID,
              applicationID
            }
          }
        })
      }}
    </Query>
  )
}
